import { z } from 'zod';

export const ConnectionTypes = {
    ORDER_EXCHANGE: 'order-exchange',
    RENTAL: 'rental',
} as const;
export type ConnectionType = (typeof ConnectionTypes)[keyof typeof ConnectionTypes];

export const PartnerConnectionCreationFlows = {
    INVITATION_FLOW: 'invitation-flow',
    ACCOUNT_ID_FLOW: 'account-id-flow',
} as const;
export type PartnerConnectionCreationFlow =
    (typeof PartnerConnectionCreationFlows)[keyof typeof PartnerConnectionCreationFlows];

export const DeletionReasons = {
    MANUAL_USER_DELETION: 'manual-user-deletion',
    DUPLICATE_ENTRY: 'duplicate-entry',
} as const;
export type DeletionReason = (typeof DeletionReasons)[keyof typeof DeletionReasons];

export type AccountProfile = {
    id: string;
    displayName: string;
};

export type Partner = {
    id: string;
    name: string;
    description?: string;
    status: string;
    connectionType: ConnectionType;
    creationFlow?: PartnerConnectionCreationFlow;
    accountProfile?: AccountProfile;
    partnerConnectionDisplayName?: string;
    connectedAt?: string;
    deletionReason?: DeletionReason;
    deletedAt?: string;
};

export type PartnerList = {
    items: Partner[];
};

export type PartnerConnectionType = {
    connectionType: ConnectionType;
    displayName: string;
    connectionFeatures: {
        [key: string]: string[];
    };
};

export type PartnerConnectionTypeList = {
    items: PartnerConnectionType[];
};

export type Invitation = {
    id: string;
    partnerId: string;
    status: string;
    connectionType: ConnectionType;
    redirectUri?: string;
};

export type InvitationRedirect = {
    redirectUri: string;
};

export type InvitationQuery = {
    id: string | undefined;
    partnerId: string | undefined;
};

export type InvitationRequest = {
    partnerId: string;
    invitationRedirectUri: string;
};

export type EmailInvitation = {
    partnerId: string;
    invitationId: string;
    emailAddress: string;
    inviteeName: string;
    language: string;
};

// Zod Schemas

export const ApiAccountProfileSchema = z.object({
    id: z.string(),
    display_name: z.string(),
});

export const ApiPartnerSchema = z
    .object({
        id: z.string(),
        name: z.string(),
        status: z.string(),
        connection_type: z.enum(ConnectionTypes),
        creation_flow: z.enum(PartnerConnectionCreationFlows).optional(),
        description: z.string().optional(),
        account_profile: ApiAccountProfileSchema.optional(),
        connected_at: z.string().optional(),
        deletion_reason: z.enum(DeletionReasons).optional(),
        deleted_at: z.string().optional(),
    })
    .superRefine((partner, ctx) => {
        if (partner.creation_flow === PartnerConnectionCreationFlows.ACCOUNT_ID_FLOW && partner.status === 'pending') {
            ctx.addIssue({
                code: 'custom',
                message: 'account-id-flow partner connections must not be pending',
                path: ['status'],
            });
        }
    });

export const ApiPartnerListSchema = z.object({
    items: z.array(ApiPartnerSchema),
});

export const ApiPartnerConnectionTypeSchema = z.object({
    connection_type: z.enum(ConnectionTypes),
    display_name: z.string(),
    connection_features: z.record(z.string(), z.array(z.string())),
});

export const ApiPartnerConnectionTypeListSchema = z.object({
    items: z.array(ApiPartnerConnectionTypeSchema),
});

export const ApiInvitationSchema = z.object({
    id: z.string(),
    partner_id: z.string(),
    status: z.string(),
    connection_type: z.enum(ConnectionTypes),
    redirect_uri: z.string().optional(),
});

export const ApiInvitationRedirectSchema = z.object({
    redirect_uri: z.string(),
});

export const ApiEmailInvitationSchema = z.object({
    partner_id: z.string(),
    invitation_id: z.string(),
    email_address: z.string(),
    invitee_name: z.string(),
    language: z.string(),
});
export const ProblemSchema = z.object({
    detail: z.string().optional(),
});
export const isProblem = (p: unknown): p is Problem => ProblemSchema.safeParse(p).success;

// Inferred Types
export type ApiAccountProfile = z.infer<typeof ApiAccountProfileSchema>;
export type ApiPartner = z.infer<typeof ApiPartnerSchema>;
export type ApiPartnerList = z.infer<typeof ApiPartnerListSchema>;
export type ApiPartnerConnectionType = z.infer<typeof ApiPartnerConnectionTypeSchema>;
export type ApiPartnerConnectionTypeList = z.infer<typeof ApiPartnerConnectionTypeListSchema>;
export type ApiInvitation = z.infer<typeof ApiInvitationSchema>;
export type ApiInvitationRedirect = z.infer<typeof ApiInvitationRedirectSchema>;
export type ApiEmailInvitation = z.infer<typeof ApiEmailInvitationSchema>;
export type Problem = z.infer<typeof ProblemSchema>;
